if (!customElements.get('pastael-packed-by-you-filters')) {
  class PastaelPackedByYouFilters extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.pastael-packed-by-you');
      if (!this.section) return;

      this.productFilterButtons = Array.from(this.querySelectorAll('[data-product-filter]'));
      this.colorFiltersContainer = this.querySelector('[data-color-filters]');
      this.cards = Array.from(this.section.querySelectorAll('[data-look-card]'));

      const defaultProductFilter = this.productFilterButtons.find((button) =>
        button.classList.contains('is-active')
      );
      this.activeProductFilter = defaultProductFilter?.dataset.productFilter || '';
      this.activeColorFilter = 'all';

      this.grid = this.section.querySelector('[data-look-grid]');
      this.promo = this.grid?.querySelector('[data-promo-banner]');
      this.onResize = this.onResize.bind(this);

      this.buildColorFilters();
      this.bindProductFilters();
      this.resetChipVisibility();
      this.applyFilters();
      window.addEventListener('resize', this.onResize, { passive: true });
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
    }

    onResize() {
      this.repositionPromo();
    }

    getPromoColumnCount() {
      if (!this.grid) return 3;

      const raw =
        this.grid.style.getPropertyValue('--pastael-packed-by-you-columns') ||
        getComputedStyle(this.grid).getPropertyValue('--pastael-packed-by-you-columns');
      const columns = parseInt(raw, 10);

      return Number.isFinite(columns) && columns > 0 ? columns : 3;
    }

    getVisualColumnCount() {
      if (!window.matchMedia('(min-width: 750px)').matches) return 1;
      return this.getPromoColumnCount();
    }

    repositionPromo() {
      if (!this.grid || !this.promo) return;

      const promoColumns = this.getPromoColumnCount();
      const visualColumns = this.getVisualColumnCount();
      const promoAfterRow = parseInt(this.grid.dataset.promoAfterRow || '2', 10) || 2;
      const insertAfterVisibleCount = Math.max(promoAfterRow, 1) * promoColumns;
      const visibleCards = Array.from(this.grid.querySelectorAll('[data-look-card]:not(.is-hidden)'));

      if (!visibleCards.length) {
        this.promo.classList.add('is-hidden');
        return;
      }

      this.promo.classList.remove('is-hidden');

      this.grid.querySelectorAll('[data-look-card].is-row-fill').forEach((card) => {
        card.classList.remove('is-row-fill');
      });
      this.grid.removeAttribute('data-incomplete-promo-row');

      const insertAfter = Math.min(insertAfterVisibleCount, visibleCards.length);
      visibleCards[insertAfter - 1].insertAdjacentElement('afterend', this.promo);

      const rowCards = visibleCards.slice(0, insertAfter);
      const remainder = rowCards.length % visualColumns;
      if (remainder > 0) {
        this.grid.dataset.incompletePromoRow = 'true';
        rowCards.slice(-remainder).forEach((card) => {
          card.classList.add('is-row-fill');
        });
      }
    }

    resetChipVisibility() {
      this.section.querySelectorAll('.pastael-product-chip.is-hidden').forEach((chip) => {
        chip.classList.remove('is-hidden');
      });
    }

    buildColorFilters() {
      if (!this.colorFiltersContainer) return;

      const existingButtons = Array.from(
        this.colorFiltersContainer.querySelectorAll('[data-color-filter]')
      );

      if (existingButtons.length > 0) {
        this.colorFilterButtons = existingButtons;
        this.bindColorFilters();
        return;
      }

      const colorMap = new Map();

      this.section.querySelectorAll('[data-chip-item][data-color-slug]').forEach((source) => {
        const slug = source.dataset.colorSlug;
        if (!slug || colorMap.has(slug)) return;

        colorMap.set(slug, {
          slug,
          label: source.dataset.colorLabel || slug,
          swatchStyle: source.dataset.swatchStyle || '',
        });
      });

      colorMap.forEach((color) => {
        this.colorFiltersContainer.appendChild(
          this.createColorButton(color.slug, color.label, color.swatchStyle, false)
        );
      });

      this.colorFilterButtons = Array.from(
        this.colorFiltersContainer.querySelectorAll('[data-color-filter]')
      );
      this.bindColorFilters();
    }

    createColorButton(slug, label, swatchStyle, isActive) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `pastael-packed-by-you__color-filter${isActive ? ' is-active' : ''}`;
      button.dataset.colorFilter = slug;
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      button.setAttribute('aria-label', label);

      const inner = document.createElement('span');
      inner.className = 'pastael-packed-by-you__color-filter-inner';

      const swatch = document.createElement('span');
      swatch.className = 'pastael-packed-by-you__color-swatch';
      if (swatchStyle) {
        swatch.style.setProperty('--swatch--background', swatchStyle);
      }

      inner.appendChild(swatch);
      button.appendChild(inner);
      return button;
    }

    bindProductFilters() {
      this.productFilterButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const filter = button.dataset.productFilter || '';
          const isSame = this.activeProductFilter === filter;
          this.activeProductFilter = isSame ? '' : filter;
          this.productFilterButtons.forEach((item) => {
            const isActive = !isSame && item === button;
            item.classList.toggle('is-active', isActive);
            item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          });
          this.applyFilters();
        });
      });
    }

    bindColorFilters() {
      this.colorFilterButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const slug = button.dataset.colorFilter || 'all';
          const isSame = this.activeColorFilter === slug;
          this.activeColorFilter = isSame ? 'all' : slug;

          this.colorFilterButtons.forEach((item) => {
            const isActive = !isSame && item === button;
            item.classList.toggle('is-active', isActive);
            item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          });
          this.applyFilters();
        });
      });
    }

    getActiveColorFilter() {
      return this.activeColorFilter || 'all';
    }

    colorMatches(slug, filterSlug) {
      if (!slug || !filterSlug) return false;
      return slug === filterSlug || slug.includes(filterSlug) || filterSlug.includes(slug);
    }

    applyFilters() {
      this.cards.forEach((card) => {
        const cardFilters = (card.dataset.productFilter || '')
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean);
        const productMatch =
          !this.activeProductFilter ||
          cardFilters.includes('all') ||
          cardFilters.length === 0 ||
          cardFilters.includes(this.activeProductFilter);

        let colorMatch = this.activeColorFilter === 'all';

        if (!colorMatch) {
          const cardColors = (card.dataset.colors || '')
            .split(',')
            .map((value) => value.trim())
            .filter(Boolean);

          colorMatch = cardColors.some((color) =>
            this.colorMatches(color, this.activeColorFilter)
          );

          if (!colorMatch) {
            colorMatch = Array.from(card.querySelectorAll('[data-chip-item]')).some((item) =>
              this.colorMatches(item.dataset.colorSlug || '', this.activeColorFilter)
            );
          }
        }

        card.classList.toggle('is-hidden', !(productMatch && colorMatch));
      });

      this.resetChipVisibility();
      this.repositionPromo();
      this.refreshSetButtons();
    }

    refreshSetButtons() {
      this.section?.querySelectorAll('pastael-community-set-form').forEach((form) => {
        form.refreshAvailability?.();
      });
    }
  }

  customElements.define('pastael-packed-by-you-filters', PastaelPackedByYouFilters);
}
